import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Package, Plus, Edit3, X, Trash2, Check, Star } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../api/client';

function usePlanos() {
  return useQuery({
    queryKey: ['billing-planos-mgmt'],
    queryFn: async () => {
      const { data } = await api.get('/billing/planos');
      return data;
    },
  });
}

const fmtBRL = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const PLAN_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#0891b2'];

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 14px', borderRadius: 8,
  border: '1px solid var(--border-color, #d1d5db)', background: 'var(--bg-card, white)',
  color: 'var(--text-main, #111)', fontSize: '.88rem',
};
const labelStyle: React.CSSProperties = { display: 'block', fontSize: '.76rem', fontWeight: 600, marginBottom: 6, color: 'var(--text-muted, #6b7280)' };

type PlanoForm = {
  nome: string;
  descricao: string;
  precoBaseEscola: number;
  limiteEscolas: number | null;
  limiteAlunos: number | null;
  features: string[];
};

const emptyForm: PlanoForm = { nome: '', descricao: '', precoBaseEscola: 0, limiteEscolas: null, limiteAlunos: null, features: [] };

export function MasterPlanosPage() {
  const qc = useQueryClient();
  const { data: planos, isLoading } = usePlanos();
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<PlanoForm>(emptyForm);
  const [newFeature, setNewFeature] = useState('');

  const totalPlanos = planos?.length || 0;
  const totalAss = planos?.reduce((a: number, p: any) => a + (p._count?.assinaturas || 0), 0) || 0;

  const openCreate = () => { setEditId(null); setForm(emptyForm); setNewFeature(''); setShowModal(true); };
  const openEdit = (p: any) => {
    setEditId(p.id);
    setForm({
      nome: p.nome, descricao: p.descricao || '', precoBaseEscola: p.precoBaseEscola,
      limiteEscolas: p.limiteEscolas, limiteAlunos: p.limiteAlunos, features: p.features || [],
    });
    setNewFeature('');
    setShowModal(true);
  };

  const criarMut = useMutation({
    mutationFn: async () => {
      const body: any = { nome: form.nome, descricao: form.descricao, precoBaseEscola: form.precoBaseEscola, features: form.features };
      if (form.limiteEscolas) body.limiteEscolas = form.limiteEscolas;
      if (form.limiteAlunos) body.limiteAlunos = form.limiteAlunos;
      const { data } = await api.post('/billing/planos', body);
      return data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['billing-planos-mgmt'] }); setShowModal(false); },
  });

  const editarMut = useMutation({
    mutationFn: async () => {
      const body: any = { nome: form.nome, descricao: form.descricao, precoBaseEscola: form.precoBaseEscola, features: form.features };
      if (form.limiteEscolas) body.limiteEscolas = form.limiteEscolas;
      if (form.limiteAlunos) body.limiteAlunos = form.limiteAlunos;
      const { data } = await api.put(`/billing/planos/${editId}`, body);
      return data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['billing-planos-mgmt'] }); setShowModal(false); },
  });

  const desativarMut = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/billing/planos/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['billing-planos-mgmt'] }),
  });

  const addFeature = () => {
    const f = newFeature.trim();
    if (f && !form.features.includes(f)) { setForm({ ...form, features: [...form.features, f] }); setNewFeature(''); }
  };

  const removeFeature = (idx: number) => setForm({ ...form, features: form.features.filter((_, i) => i !== idx) });

  const isPending = criarMut.isPending || editarMut.isPending;
  const error = criarMut.error || editarMut.error;

  return (
    <div className="admin-page animate-fadeIn">

      {/* ═══ HERO BANNER ═══ */}
      <div style={{
        background: 'linear-gradient(135deg, #4c1d95 0%, #6d28d9 40%, #7c3aed 100%)',
        borderRadius: 20, padding: '28px 32px', marginBottom: 24, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(167,139,250,.12)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <Package size={18} style={{ color: '#c4b5fd' }} />
                <span style={{ color: 'rgba(255,255,255,.4)', fontSize: '.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.12em' }}>Catálogo</span>
              </div>
              <h1 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 800, margin: 0 }}>Planos Comerciais</h1>
              <p style={{ color: 'rgba(255,255,255,.45)', fontSize: '.82rem', marginTop: 4 }}>
                Gerencie os planos oferecidos para escolas, municípios e estados
              </p>
            </div>
            <button onClick={openCreate} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 10,
              background: '#c4b5fd', color: '#4c1d95', fontWeight: 700, fontSize: '.85rem', border: 'none', cursor: 'pointer',
            }}>
              <Plus size={16} /> Novo Plano
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, marginTop: 24 }}>
            {[
              { icon: Package, value: totalPlanos, label: 'Planos Ativos', color: '#c4b5fd' },
              { icon: Star, value: totalAss, label: 'Assinaturas Vinculadas', color: '#fbbf24' },
              { icon: Edit3, value: 'CRUD', label: 'Gestão Completa', color: '#10b981' },
            ].map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 48, height: 48, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${s.color}20`, color: s.color }}>
                  <s.icon size={24} />
                </div>
                <div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 900, color: 'white', lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: '.75rem', color: 'rgba(255,255,255,.6)', fontWeight: 500 }}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ GRID DE PLANOS ═══ */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Carregando planos...</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {(planos || []).map((p: any, idx: number) => {
            const color = PLAN_COLORS[idx % PLAN_COLORS.length];
            const assCount = p._count?.assinaturas || 0;
            return (
              <div key={p.id} style={{
                background: 'var(--bg-card, white)', borderRadius: 16, overflow: 'hidden',
                border: '1px solid var(--border-color, #e5e7eb)', position: 'relative',
              }}>
                {/* Color top bar */}
                <div style={{ height: 4, background: `linear-gradient(90deg, ${color}, ${color}88)` }} />

                <div style={{ padding: 20 }}>
                  {/* Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                    <div>
                      <h3 style={{ fontSize: '1.1rem', fontWeight: 800, margin: 0, color: 'var(--text-main)' }}>{p.nome}</h3>
                      <p style={{ fontSize: '.75rem', color: 'var(--text-muted)', margin: '4px 0 0', lineHeight: 1.3 }}>{p.descricao || 'Sem descrição'}</p>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => openEdit(p)} title="Editar" style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border-color)', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                        <Edit3 size={14} />
                      </button>
                      <button onClick={() => { if (confirm(`Desativar plano "${p.nome}"?`)) desativarMut.mutate(p.id); }} title="Desativar" style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border-color)', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444' }}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  {/* Preço */}
                  <div style={{ marginBottom: 16 }}>
                    <span style={{ fontSize: '1.8rem', fontWeight: 900, color }}>
                      {fmtBRL(p.precoBaseEscola)}
                    </span>
                    <span style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginLeft: 4 }}>/escola/mês</span>
                  </div>

                  {/* Limites */}
                  <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                    <div style={{ padding: '6px 12px', borderRadius: 8, background: 'var(--bg-main, #f3f4f6)', fontSize: '.75rem', color: 'var(--text-muted)' }}>
                      <strong style={{ color: 'var(--text-main)' }}>{p.limiteEscolas || '∞'}</strong> escolas
                    </div>
                    <div style={{ padding: '6px 12px', borderRadius: 8, background: 'var(--bg-main, #f3f4f6)', fontSize: '.75rem', color: 'var(--text-muted)' }}>
                      <strong style={{ color: 'var(--text-main)' }}>{p.limiteAlunos ? p.limiteAlunos.toLocaleString('pt-BR') : '∞'}</strong> alunos
                    </div>
                    <div style={{ padding: '6px 12px', borderRadius: 8, background: `${color}10`, fontSize: '.75rem', color }}>
                      <strong>{assCount}</strong> assinatura{assCount !== 1 ? 's' : ''}
                    </div>
                  </div>

                  {/* Features */}
                  {p.features && p.features.length > 0 && (
                    <div style={{ borderTop: '1px solid var(--border-color, #f3f4f6)', paddingTop: 12 }}>
                      {p.features.map((f: string, i: number) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: '.78rem', color: 'var(--text-main)' }}>
                          <Check size={12} style={{ color: '#10b981', flexShrink: 0 }} />
                          {f}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ MODAL CRIAR/EDITAR PLANO ═══ */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div style={{ background: 'var(--bg-card, white)', borderRadius: 16, width: 520, maxHeight: '85vh', overflow: 'auto', padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 800, margin: 0, color: 'var(--text-main)' }}>
                {editId ? 'Editar Plano' : 'Novo Plano'}
              </h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>

            {/* Nome */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Nome do Plano</label>
              <input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Ex: Premium" style={inputStyle} />
            </div>

            {/* Descrição */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Descrição</label>
              <textarea value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} placeholder="Descrição do plano..." rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
            </div>

            {/* Preço */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Preço por Escola/Mês (R$)</label>
              <input
                inputMode="numeric"
                pattern="[0-9]*"
                value={form.precoBaseEscola || ''}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '');
                  setForm({ ...form, precoBaseEscola: digits ? Number(digits) : 0 });
                }}
                placeholder="490"
                style={inputStyle}
              />
            </div>

            {/* Limites lado a lado */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Limite de Escolas (vazio = ilimitado)</label>
                <input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={form.limiteEscolas || ''}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '');
                    setForm({ ...form, limiteEscolas: digits ? Number(digits) : null });
                  }}
                  placeholder="∞"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Limite de Alunos (vazio = ilimitado)</label>
                <input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={form.limiteAlunos || ''}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '');
                    setForm({ ...form, limiteAlunos: digits ? Number(digits) : null });
                  }}
                  placeholder="∞"
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Features */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Features / Recursos ({form.features.length})</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input value={newFeature} onChange={(e) => setNewFeature(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addFeature(); } }}
                  placeholder="Ex: Dashboard municipal" style={{ ...inputStyle, flex: 1 }} />
                <button onClick={addFeature} style={{ padding: '0 16px', borderRadius: 8, border: 'none', background: '#6366f1', color: 'white', fontWeight: 700, cursor: 'pointer' }}>
                  <Plus size={16} />
                </button>
              </div>
              {form.features.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {form.features.map((f, i) => (
                    <span key={i} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px',
                      borderRadius: 6, background: 'var(--bg-main, #f3f4f6)', fontSize: '.78rem', color: 'var(--text-main)',
                    }}>
                      <Check size={10} style={{ color: '#10b981' }} /> {f}
                      <button onClick={() => removeFeature(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#ef4444', lineHeight: 1 }}><X size={12} /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Ações */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button onClick={() => setShowModal(false)} style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'transparent', color: 'var(--text-main)', fontWeight: 600, cursor: 'pointer' }}>Cancelar</button>
              <button
                onClick={() => editId ? editarMut.mutate() : criarMut.mutate()}
                disabled={!form.nome || !form.precoBaseEscola || isPending}
                style={{
                  padding: '10px 20px', borderRadius: 8, border: 'none', fontWeight: 700, cursor: 'pointer',
                  background: (!form.nome || !form.precoBaseEscola) ? '#d1d5db' : '#6366f1', color: 'white',
                }}
              >
                {isPending ? 'Salvando...' : editId ? 'Salvar Alterações' : 'Criar Plano'}
              </button>
            </div>

            {error && (
              <div style={{ marginTop: 12, padding: '8px 14px', borderRadius: 8, background: '#fee2e2', color: '#991b1b', fontSize: '.78rem' }}>
                Erro: {(error as any)?.message || 'Falha'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
